import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Plus, Search } from 'lucide-react';

import { ApiError, api } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar } from '@/components/ui/avatar';
import { avatarUrl } from '@/lib/avatar-url';
import type { AdminUserSummary } from './types';
import { roleLabel } from './types';

export function AdminUsersList() {
  const [q, setQ] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const navigate = useNavigate();

  const users = useQuery({
    queryKey: ['admin', 'users', { includeInactive }],
    queryFn: () =>
      api<AdminUserSummary[]>(
        `/admin/users${includeInactive ? '?includeInactive=true' : ''}`,
      ),
  });

  const filtered = useMemo(() => {
    const data = users.data ?? [];
    if (!q.trim()) return data;
    const needle = q.trim().toLowerCase();
    return data.filter(
      (u) =>
        u.email.toLowerCase().includes(needle) ||
        u.fullName.toLowerCase().includes(needle) ||
        u.employeeCode?.toLowerCase().includes(needle),
    );
  }, [users.data, q]);

  return (
    <div className="p-6 md:p-8">
      <div className="bg-card border border-border rounded-sm shadow-md overflow-hidden">
        <header className="px-7 py-5 border-b border-border flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-lg font-semibold tracking-tightish">
              Empleados
              <span className="text-muted-foreground font-normal"> · {filtered.length}</span>
            </h2>
            <p className="text-[13px] text-muted-foreground mt-1">
              Gestión de empleados de Icaria Atelier.
            </p>
          </div>
          <div className="inline-flex items-center gap-3 flex-wrap">
            <div className="relative w-72 max-w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                type="search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Nombre, email o código…"
                className="pl-9"
              />
            </div>
            <label className="inline-flex items-center gap-2 text-[13px] font-medium text-foreground cursor-pointer">
              <input
                type="checkbox"
                checked={includeInactive}
                onChange={(e) => setIncludeInactive(e.target.checked)}
                className="h-4 w-4 accent-icaria-600"
              />
              Inactivos
            </label>
            <Button asChild variant="primary" size="sm">
              <Link to="/admin/users/new">
                <Plus className="h-3.5 w-3.5" />
                Nuevo empleado
              </Link>
            </Button>
          </div>
        </header>

        {users.isLoading ? (
          <p className="px-7 py-10 text-center text-sm text-muted-foreground">Cargando…</p>
        ) : users.error ? (
          <p role="alert" className="px-7 py-10 text-center text-sm text-destructive">
            {users.error instanceof ApiError ? users.error.message : 'Error cargando empleados'}
          </p>
        ) : filtered.length === 0 ? (
          <p className="px-7 py-10 text-center text-sm text-muted-foreground">
            No hay empleados que coincidan.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr>
                  <Th>Empleado</Th>
                  <Th>Email</Th>
                  <Th>Rol</Th>
                  <Th>Código</Th>
                  <Th>Estado</Th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((u) => (
                  <tr
                    key={u.id}
                    className="border-b border-border cursor-pointer hover:bg-paper transition-colors"
                    onClick={() => navigate(`/admin/users/${u.id}`)}
                  >
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        <Avatar
                          fullName={u.fullName}
                          color={u.profileColor}
                          avatarUrl={avatarUrl(u.id, u.avatarUpdatedAt)}
                          muted={Boolean(u.deletedAt) || !u.isActive}
                        />
                        <Link
                          to={`/admin/users/${u.id}`}
                          className="text-[14px] font-semibold text-foreground hover:text-icaria-600"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {u.fullName}
                        </Link>
                      </div>
                    </td>
                    <td className="px-5 py-4 font-mono text-[12px] text-muted-foreground truncate max-w-[260px]">
                      {u.email}
                    </td>
                    <td className="px-5 py-4 text-[13px]">{roleLabel(u.role)}</td>
                    <td className="px-5 py-4 font-mono text-[12px] text-muted-foreground">
                      {u.employeeCode ?? '—'}
                    </td>
                    <td className="px-5 py-4">
                      {u.deletedAt ? (
                        <Badge tone="neutral">Inactivo</Badge>
                      ) : u.isActive ? (
                        <Badge tone="green" dot>Activo</Badge>
                      ) : (
                        <Badge tone="neutral">Desactivado</Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th
      className="font-mono text-[12px] font-medium uppercase text-muted-foreground bg-paper border-b border-border px-5 py-3.5 text-left"
      style={{ letterSpacing: '0.08em' }}
    >
      {children}
    </th>
  );
}

